import { type PersonWithHousehold } from "@/hooks/usePeople";
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";
import { isNewThisMonth } from "@/lib/people-aggregation";

export type NewPeopleThisMonthContext = {
  asOf: Date;
  /**
   * The live instant. `asOf` anchors the month but the report page passes the organization's
   * midnight, so without this a person added later the same day would be missing from the report
   * while the Everyone "New this month" view (bounded by the live instant) lists them.
   */
  liveNow?: Date;
  /**
   * The organization's IANA time zone. When given, "this month" is the organization's calendar
   * month at the live instant, exactly as the Everyone "New this month" filter decides it, so the
   * two cannot disagree for a viewer in another zone.
   */
  timeZone?: string | null;
  people: PersonWithHousehold[];
  engagementByPerson?: Record<string, { lastAttendanceDate: string | null; totalGiving: number }>;
};

const buildPersonName = (person: PersonWithHousehold) => {
  if (person.entity_type === "company" && person.company_name) {
    return person.company_name;
  }
  return `${person.first_name || ""} ${person.last_name || ""}`.trim() || "Unknown";
};

/**
 * Coerce an upstream numeric (which may arrive as NaN or a string from
 * deserialization) to a finite number. The previous implementation used
 * `?? 0` which only handles null/undefined; it lets NaN and string-numerics
 * through, which propagate into row.totalGiving and break "sort by giving".
 */
const toFiniteNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const isValidIsoDateString = (value: unknown): value is string => {
  if (typeof value !== "string" || value.length === 0) return false;
  const t = new Date(value).getTime();
  return Number.isFinite(t);
};

/**
 * Title-case a status with `_` or `-` separators. Trims leading/trailing
 * whitespace first so we don't render `" Active "` or double-space artifacts.
 */
const formatStatus = (status: string | null | undefined): string | null => {
  if (typeof status !== "string") return null;
  const trimmed = status.trim();
  if (trimmed.length === 0) return null;
  return trimmed
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export function buildNewPeopleThisMonthAggregation({
  asOf,
  liveNow,
  timeZone,
  people,
  engagementByPerson,
}: NewPeopleThisMonthContext): ReportAggregation {
  const startOfMonth = new Date(asOf.getFullYear(), asOf.getMonth(), 1);

  const upperBound = liveNow && liveNow.getTime() > asOf.getTime() ? liveNow : asOf;

  const safePeople = Array.isArray(people) ? people : [];

  const newPeople = safePeople.filter((person) => {
    if (!person?.created_at) return false;
    const createdAt = new Date(person.created_at);
    // Reject NaN-time (malformed `created_at`) instead of silently letting
    // the comparison return false — equivalent behavior here, but the
    // explicit guard makes the contract obvious and protects against
    // future logic that uses `createdAt` for ordering or display.
    if (Number.isNaN(createdAt.getTime())) return false;
    if (timeZone) {
      const instant = liveNow ?? asOf;
      return isNewThisMonth(person.created_at, instant, timeZone, instant);
    }
    return createdAt >= startOfMonth && createdAt <= upperBound;
  });

  const rows = newPeople
    .map((person) => {
      const engagement = engagementByPerson?.[person.id];
      const lastAttendanceDate = isValidIsoDateString(engagement?.lastAttendanceDate)
        ? engagement?.lastAttendanceDate ?? null
        : null;

      return {
        id: person.id,
        label: buildPersonName(person),
        email: person.email || null,
        phone: person.phone || null,
        household: person.households?.name || null,
        status: formatStatus(person.status),
        createdAt: person.created_at,
        // `_createdAtMs` is for stable sort below — held in a private
        // field that the renderer can ignore. Computed once per row to
        // avoid re-parsing the timestamp twice.
        _createdAtMs: person.created_at ? new Date(person.created_at).getTime() : 0,
        lastAttendanceDate,
        totalGiving: toFiniteNumber(engagement?.totalGiving),
        entityType: "person" as const,
        drilldownUrl: `/people/${person.id}`,
      };
    })
    // Most recently joined first (the user's natural read order); ties
    // broken deterministically by id so two members joined the same
    // millisecond don't shuffle between refetches.
    .sort((a, b) => {
      if (a._createdAtMs !== b._createdAtMs) {
        return (b._createdAtMs as number) - (a._createdAtMs as number);
      }
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });

  return {
    asOf,
    summary: [buildReportSummaryItem("report.new_people.people_count", rows.length)],
    rows,
  };
}
